import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="suggest-place-search"
//
// Step 1 of the public /suggest wizard. Searches Google Places for
// *establishments* via the modern Places API with a custom dropdown, then on
// selection POSTs the picked place — name, website, formatted address,
// coordinates — to the `check` action, which renders either "already in the
// directory" cards or the prefilled details form into the results container.
// Adapted from referral_place_search_controller.ts (same dropdown pattern).
const DEBOUNCE_MS = 200;

type SuggestionPayload = Record<string, string>;

export default class extends Controller {
  static targets = ["input", "dropdown", "listbox", "results"];

  static values = {
    checkUrl: String,
    country: { type: String, default: "us" },
  };

  declare inputTarget: HTMLInputElement;
  declare dropdownTarget: HTMLElement;
  declare listboxTarget: HTMLElement;
  declare resultsTarget: HTMLElement;

  declare checkUrlValue: string;
  declare countryValue: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  declare predictions: any[];
  declare activeIndex: number;
  declare fetchSeq: number;
  declare lastQuery: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  declare sessionToken: any;
  declare debounceHandle: ReturnType<typeof setTimeout> | null;
  declare lastPayload: SuggestionPayload | null;
  declare boundInit?: () => void;
  declare onDocumentPointerDown: (event: Event) => void;

  connect() {
    this.predictions = [];
    this.activeIndex = -1;
    this.fetchSeq = 0;
    this.lastQuery = "";
    this.sessionToken = null;
    this.debounceHandle = null;
    this.lastPayload = null;

    this.onDocumentPointerDown = (event: Event) => this.handleOutsideClick(event);
    document.addEventListener("pointerdown", this.onDocumentPointerDown);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).google?.maps?.places) {
      this.mintSessionToken();
    } else {
      this.boundInit = () => this.mintSessionToken();
      window.addEventListener("google-maps-loaded", this.boundInit);
    }
  }

  disconnect() {
    if (this.boundInit) window.removeEventListener("google-maps-loaded", this.boundInit);
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    if (this.debounceHandle) clearTimeout(this.debounceHandle);
  }

  // One session token spans typing + the selection's fetchFields; rotate after
  // each completed pick so Google bills each "search → pick" as one session.
  mintSessionToken() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Token = (window as any).google?.maps?.places?.AutocompleteSessionToken;
    this.sessionToken = Token ? new Token() : null;
  }

  // === Input lifecycle ===========================================

  onInput() {
    const query = this.inputTarget.value.trim();
    if (this.debounceHandle) clearTimeout(this.debounceHandle);

    if (query === "") {
      this.predictions = [];
      this.activeIndex = -1;
      this.renderList();
      this.closeList();
      return;
    }
    if (query === this.lastQuery) return;

    this.debounceHandle = setTimeout(() => this.fetchPredictions(query), DEBOUNCE_MS);
  }

  onFocus() {
    if (this.predictions.length > 0) this.openList();
  }

  onKeyDown(event: KeyboardEvent) {
    const key = event.key;

    if (key === "Enter") {
      event.preventDefault();
      const pick = this.predictions[this.activeIndex];
      if (pick) this.selectPrediction(pick);
      return;
    }
    if (key === "Escape") {
      event.preventDefault();
      this.closeList();
      return;
    }
    if (this.predictions.length === 0) return;

    if (key === "ArrowDown") {
      event.preventDefault();
      this.setActive((this.activeIndex + 1) % this.predictions.length);
    } else if (key === "ArrowUp") {
      event.preventDefault();
      const next = this.activeIndex <= 0 ? this.predictions.length - 1 : this.activeIndex - 1;
      this.setActive(next);
    }
  }

  // === Google Places fetch =======================================

  async fetchPredictions(query: string) {
    this.lastQuery = query;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Suggest = (window as any).google?.maps?.places?.AutocompleteSuggestion;
    if (!Suggest?.fetchAutocompleteSuggestions) return;

    const seq = ++this.fetchSeq;

    try {
      const { suggestions } = await Suggest.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: this.sessionToken,
        includedPrimaryTypes: ["establishment"],
        includedRegionCodes: [this.countryValue],
      });

      if (seq !== this.fetchSeq) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.predictions = suggestions.map((s: any) => s.placePrediction).filter(Boolean);
      this.activeIndex = -1;
      this.renderList();
      this.openList();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Places autocomplete error:", err);
      this.predictions = [];
      this.renderList();
      this.closeList();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async selectPrediction(prediction: any) {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location", "websiteURI"],
      });

      const loc = place.location;
      const lat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
      const lng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;

      const name = place.displayName || prediction.mainText?.text || prediction.text?.text || "";
      this.inputTarget.value = name;
      this.closeList();
      this.inputTarget.blur();
      this.lastQuery = name;
      this.mintSessionToken();

      const payload: SuggestionPayload = {
        "suggestion[name]": name,
        "suggestion[url]": place.websiteURI || "",
        "suggestion[address_line]": place.formattedAddress || "",
        "suggestion[latitude]": lat == null ? "" : String(lat),
        "suggestion[longitude]": lng == null ? "" : String(lng),
      };
      this.lastPayload = payload;
      this.check(payload, false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Place fetch error:", err);
    }
  }

  // "Not the same organization?" on the already-exists card — re-POST the last
  // pick past the dedup gate so the prefilled details form renders instead.
  continueAnyway() {
    if (this.lastPayload) this.check(this.lastPayload, true);
  }

  private async check(payload: SuggestionPayload, skipDedup: boolean) {
    const body = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => body.set(k, v));
    if (skipDedup) body.set("skip_dedup", "1");

    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

    try {
      const response = await fetch(this.checkUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": token,
        },
        credentials: "same-origin",
        body: body.toString(),
      });
      if (!response.ok) return;
      this.resultsTarget.innerHTML = await response.text();
      this.resultsTarget.scrollIntoView?.({ behavior: "smooth", block: "start" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("suggestion check error:", err);
    }
  }

  // === Dropdown rendering / ARIA =================================

  renderList() {
    if (this.predictions.length === 0) {
      this.listboxTarget.innerHTML = "";
      return;
    }
    this.listboxTarget.innerHTML = this.predictions.map((p, i) => this.rowMarkup(p, i)).join("");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rowMarkup(prediction: any, i: number): string {
    const isActive = i === this.activeIndex;
    const primary = this.escapeHtml(prediction.mainText?.text || prediction.text?.text || "");
    const secondary = prediction.secondaryText?.text ? this.escapeHtml(prediction.secondaryText.text) : "";
    const baseRow = "flex items-start gap-2.5 px-2 py-1.5 cursor-pointer rounded-lg transition-colors";
    const stateRow = isActive ? "bg-primary/10" : "hover:bg-base-200";

    return `
      <li id="suggest-place-option-${i}"
          role="option"
          aria-selected="${isActive}"
          data-index="${i}"
          data-action="mousedown->suggest-place-search#onRowMouseDown click->suggest-place-search#onRowClick mousemove->suggest-place-search#onRowHover"
          class="${baseRow} ${stateRow}">
        <span class="shrink-0 mt-0.5 text-base-content/40">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/>
          </svg>
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-base-content leading-snug truncate">${primary}</p>
          ${secondary ? `<p class="text-xs text-base-content/60 leading-snug truncate mt-0.5">${secondary}</p>` : ""}
        </div>
      </li>
    `;
  }

  private escapeHtml(str: string): string {
    return String(str).replace(
      /[&<>"']/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
    );
  }

  private setActive(index: number) {
    this.activeIndex = index;
    this.renderList();
    const id = `suggest-place-option-${index}`;
    this.inputTarget.setAttribute("aria-activedescendant", id);
    document.getElementById(id)?.scrollIntoView({ block: "nearest" });
  }

  private openList() {
    if (this.predictions.length === 0) {
      this.closeList();
      return;
    }
    this.dropdownTarget.classList.remove("hidden");
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  private closeList() {
    this.dropdownTarget.classList.add("hidden");
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
  }

  // === Row events ================================================

  onRowMouseDown(event: Event) {
    event.preventDefault();
  }

  onRowClick(event: Event) {
    const i = parseInt((event.currentTarget as HTMLElement).dataset.index || "", 10);
    const pick = this.predictions[i];
    if (pick) this.selectPrediction(pick);
  }

  onRowHover(event: Event) {
    const i = parseInt((event.currentTarget as HTMLElement).dataset.index || "", 10);
    if (Number.isFinite(i) && i !== this.activeIndex) this.setActive(i);
  }

  private handleOutsideClick(event: Event) {
    if (this.element.contains(event.target as Node)) return;
    this.closeList();
  }
}
